import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import CountUp from "@/components/CountUp";
import { useRoundData } from "@/components/strategy/useRound";
import LockedResults from "@/components/strategy/LockedResults";
import { Panel, PageHead, Stat, Bar, Loading, ErrorNote, ScorePill, Avatar, DemoBadge } from "@/components/strategy/ui";
import { scoreTone, PARTICIPANT_NAME } from "@/lib/strategy";

function QuestionCard({ q, tone }) {
  return (
    <div className="st-panel-2 p-4" data-testid={`align-q-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-sm leading-snug"><span className="st-faint mr-2">{q.id}</span>{q.title}</p>
        <ScorePill score={q.score} />
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        {Object.entries(q.answers).map(([pid, a]) => (
          <div key={pid} className="flex gap-2 text-xs leading-snug"><Avatar pid={pid} size={20} /><span className="st-muted">{a.label}</span></div>
        ))}
      </div>
      {tone === "diff" && <p className="text-xs mt-3 leading-relaxed" style={{ color: "var(--st-amber)" }}><span className="font-bold">Why it matters · </span><span className="st-muted">{q.stakes}</span></p>}
      {q.ai_note && <p className="st-faint text-xs mt-2 italic">AI-assessed: {q.ai_note}</p>}
    </div>
  );
}

export default function AlignmentDashboard() {
  const { data, loading, locked, error, reload } = useRoundData("analysis");
  const [showMethod, setShowMethod] = useState(false);
  if (locked) return <><PageHead eyebrow="Alignment" title="Leadership alignment" /><LockedResults /></>;
  if (loading || !data) return <Loading label="Calculating alignment" />;
  if (error) return <ErrorNote error={error} retry={reload} />;

  const qmap = Object.fromEntries(data.questions.map((q) => [q.id, q]));
  const radar = data.sections.map((s) => ({ subject: s.short, value: s.score ?? 0 }));
  const tone = scoreTone(data.overall);

  return (
    <div className="space-y-8">
      <PageHead eyebrow="Alignment" title="Leadership alignment" sub={data.partial ? "Partial results — not every founder has submitted. Scores will change." : "Calculated from all three submitted assessments."}
        actions={data.demo && <DemoBadge />} />

      <section className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
        <Panel className="p-8 sm:p-10 relative overflow-hidden st-grain" glow data-testid="align-overall">
          <div className="blob" style={{ width: 420, height: 420, top: -200, right: -120, background: `radial-gradient(circle, ${tone}2e, transparent 70%)` }} />
          <div className="relative z-10">
            <p className="st-caps">Overall leadership alignment</p>
            <p className="text-7xl sm:text-8xl font-extrabold tracking-tighter mt-3 leading-none" style={{ color: tone }}><CountUp value={data.overall ?? 0} duration={1500} />%</p>
            <p className="text-base md:text-lg font-semibold mt-4 max-w-md leading-snug">{data.interpretation}</p>
            {data.critical.length > 0 && (
              <Link to="/roadmap/decisions" className="mt-6 flex items-center gap-3 rounded-xl p-4 transition-colors" style={{ background: "rgba(240,122,122,.1)", border: "1px solid rgba(240,122,122,.3)" }} data-testid="align-critical-banner">
                <AlertTriangle size={20} style={{ color: "var(--st-red)" }} />
                <span className="text-sm"><b>{data.critical.length} critical decision{data.critical.length > 1 ? "s" : ""} unresolved</b> on IP, funding, governance or independence. A headline score cannot conceal these — review them before committing resources.</span>
              </Link>
            )}
          </div>
        </Panel>
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Strategic alignment" value={`${data.dimensions.strategic ?? "—"}%`} tone={scoreTone(data.dimensions.strategic)} hint="Vision, ownership, independence" testid="align-dim-strategic" />
          <Stat label="Commercial alignment" value={`${data.dimensions.commercial ?? "—"}%`} tone={scoreTone(data.dimensions.commercial)} hint="Revenue, pricing, go-to-market" testid="align-dim-commercial" />
          <Stat label="Operational alignment" value={`${data.dimensions.operational ?? "—"}%`} tone={scoreTone(data.dimensions.operational)} hint="Time, build order, hiring" testid="align-dim-operational" />
          <Stat label="Critical unresolved" value={data.critical.length} tone={data.critical.length ? "var(--st-red)" : "var(--st-lime)"} hint={`Critical questions below ${data.critical_threshold}%`} testid="align-critical-count" />
        </div>
      </section>

      <section className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
        <Panel className="p-6 st-recharts" data-testid="align-radar">
          <p className="st-caps mb-2">Section alignment</p>
          <div style={{ height: 300 }}>
            <ResponsiveContainer>
              <RadarChart data={radar} outerRadius="72%">
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <Radar dataKey="value" stroke="#C8F74A" fill="#C8F74A" fillOpacity={0.22} strokeWidth={2} isAnimationActive />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel className="p-6 space-y-4" data-testid="align-sections">
          <p className="st-caps">Agreement across the six sections</p>
          {data.sections.map((s) => (
            <div key={s.id} data-testid={`align-section-${s.id}`}>
              <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-semibold">{s.id} · {s.name}</span><span className="font-extrabold text-sm" style={{ color: scoreTone(s.score) }}>{s.score ?? "—"}%</span></div>
              <Bar value={s.score} color={scoreTone(s.score)} />
            </div>
          ))}
        </Panel>
      </section>

      {data.critical.length > 0 && (
        <section>
          <h2 className="text-base md:text-lg font-bold mb-3 flex items-center gap-2"><AlertTriangle size={18} style={{ color: "var(--st-red)" }} /> Critical unresolved decisions</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {data.critical.map((c) => (
              <div key={c.id} className="st-panel p-5" style={{ borderColor: "rgba(240,122,122,.3)" }} data-testid={`align-critical-${c.id}`}>
                <div className="flex items-start justify-between gap-3"><p className="font-semibold text-sm leading-snug"><span className="st-faint mr-2">{c.id}</span>{c.title}</p><ScorePill score={c.score} /></div>
                <div className="space-y-1.5 mt-3">{Object.entries(c.positions).map(([pid, pos]) => <p key={pid} className="text-xs flex gap-2"><span className="font-bold w-10 shrink-0" style={{ color: `var(--st-${pid === "jon" ? "lime" : pid === "andy" ? "blue" : "amber"})` }}>{PARTICIPANT_NAME[pid]}</span><span className="st-muted">{pos}</span></p>)}</div>
                <p className="st-faint text-xs mt-3 leading-relaxed">{c.stakes}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-base md:text-lg font-bold mb-3">Strongest areas of agreement</h2>
          <div className="space-y-3">{data.agreements.length ? data.agreements.map((id) => <QuestionCard key={id} q={qmap[id]} tone="agree" />) : <p className="st-muted text-sm">No question reached 70% agreement yet.</p>}</div>
        </div>
        <div>
          <h2 className="text-base md:text-lg font-bold mb-3">Biggest differences</h2>
          <div className="space-y-3">{data.differences.map((id) => <QuestionCard key={id} q={qmap[id]} tone="diff" />)}</div>
        </div>
      </section>

      <Panel className="p-6">
        <button onClick={() => setShowMethod((s) => !s)} className="flex items-center justify-between w-full text-left" data-testid="align-method-toggle">
          <span><p className="font-bold">How this score is calculated</p><p className="st-muted text-xs mt-1">Scoring model v{data.scoring_version} · question bank v{data.question_version} · deterministic; written answers AI-assisted; unavailable items excluded, never zero.</p></span>
          {showMethod ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        {showMethod && (
          <div className="mt-5 overflow-x-auto">
            <table className="st-table" data-testid="align-method-table">
              <thead><tr><th>Q</th><th>Question</th><th>Method</th><th>Weight</th><th>Contribution</th><th>Score</th></tr></thead>
              <tbody>
                {data.questions.map((q) => (
                  <tr key={q.id}>
                    <td className="st-faint">{q.id}{q.critical && <span className="ml-1" style={{ color: "var(--st-red)" }}>•</span>}</td>
                    <td className="font-medium">{q.title}</td>
                    <td className="st-muted text-xs">{q.method}{q.confidence === "ai" && " · lower confidence"}{q.confidence === "unavailable" && " · excluded"}</td>
                    <td>{q.weight}</td>
                    <td>{q.contribution}%</td>
                    <td><ScorePill score={q.score} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
